import type { RouterConfig } from '@nuxt/schema'
import type { RouteRecordRaw, RouteLocationNormalized, RouterScrollBehavior } from 'vue-router'
import { isChangingPage } from '#app/components/utils'
import { appPageTransition as defaultPageTransition } from '#build/nuxt.config.mjs'

type ScrollPosition = Awaited<ReturnType<RouterScrollBehavior>>

function _getHashElementScrollMarginTop(selector: string): number {
  try {
    const elem = document.querySelector(selector)
    if (elem) {
      return (Number.parseFloat(getComputedStyle(elem).scrollMarginTop) || 0) + (Number.parseFloat(getComputedStyle(document.documentElement).scrollPaddingTop) || 0)
    }
  } catch {
    // ignore any errors parsing scrollMarginTop
  }
  return 0
}

function _calculatePosition(to: RouteLocationNormalized, scrollBehaviorType: ScrollBehavior, position?: ScrollPosition): ScrollPosition {
  // Handle saved position for backward/forward navigation
  if (position) {
    return position
  }

  // Scroll to the element specified in the URL hash, if present
  if (to.hash) {
    return {
      el: to.hash,
      top: _getHashElementScrollMarginTop(to.hash),
      behavior: scrollBehaviorType,
    }
  }

  // Default scroll to the top left of the page
  return { left: 0, top: 0, behavior: scrollBehaviorType }
}

type ScrollOptions = {
  scroll: ScrollPosition
}

declare module 'vue-router' {
  interface _RouteLocationBase {
    options?: ScrollOptions
  }

  interface RouteLocationOptions {
    options?: ScrollOptions
  }

  interface PageMeta {
    hallSortOrder?: ReturnType<typeof makeHallSortOrderMeta>
  }
}

const makeHallSortOrderMeta = <const T extends readonly string[]>(sortOrderParameter: string, validSortOrderParameters: T, defaultSortOrder: T[number]) => {
  return {
    sortOrderParameter,
    validSortOrderParameters,
    defaultSortOrder,
  }
}

const hallSortOrder = makeHallSortOrderMeta('so', ['0', '1', '2', '4', '8'], '0')

export default <RouterConfig>{
  strict: true,
  sensitive: true,
  scrollBehaviorType: 'smooth',
  // Original: https://github.com/nuxt/nuxt/blob/5a8a6924d895b4f53f9d59edcffed7e2bf09cabd/packages/nuxt/src/pages/runtime/router.options.ts#L14-L50
  scrollBehavior(to, from, savedPosition) {
    const nuxtApp = useNuxtApp()
    // @ts-expect-error untyped, nuxt-injected option
    const behavior = useRouter().options?.scrollBehaviorType ?? 'auto'
    // Firefox will abort smooth scroll when layout changes during scrolling, which is why we need to scroll instantly instead.
    const stableBehavior = 'MozOrient' in document.documentElement.style ? 'auto' : behavior

    // By default when the returned position is falsy or an empty object, vue-router will retain the current scroll position
    // savedPosition is only available for popstate navigations (back button)
    let position: ScrollPosition = savedPosition || undefined

    const routeAllowsScrollToTop = typeof to.meta.scrollToTop === 'function' ? to.meta.scrollToTop(to, from) : to.meta.scrollToTop

    // Scroll to top if route is changed by default
    if (!position && from && to && routeAllowsScrollToTop !== false && isChangingPage(to, from)) {
      position = { left: 0, top: 0 }
    }

    const customScroll = to.options?.scroll

    // Hash routes on the same page, no page hook is fired so resolve here
    if (to.path === from.path) {
      // Flush any DOM changes before performing scrolling
      return nextTick()
        .then(() => {
          if (customScroll === undefined) {
            if (savedPosition) {
              return savedPosition
            }

            if (to.hash) {
              return { el: to.hash, top: _getHashElementScrollMarginTop(to.hash), behavior }
            }

            if (to.fullPath === from.fullPath) {
              return { left: 0, top: 0, behavior }
            }

            return { left: 0, top: 0 }
          }

          return customScroll ? { behavior: stableBehavior, ...customScroll } : customScroll
        })
        .then((returnValue) => {
          if (!to.hash) {
            return returnValue
          }

          return new Promise((resolve) => {
            let disposed = false

            const timeout = setTimeout(() => {
              disposed = true
              resolve(returnValue)
            }, 150)

            const poll = () => {
              if (disposed) {
                return
              }

              const elementOffset = document.querySelector(to.hash)?.getBoundingClientRect().top ?? 0
              if (document.body.scrollHeight >= elementOffset + window.scrollY) {
                clearTimeout(timeout)
                resolve(returnValue)
              } else {
                requestAnimationFrame(poll)
              }
            }

            poll()
          })
        })
    }

    // Wait for `page:transition:finish` or `page:loading:end` depending on if transitions are enabled or not
    const hasTransition = (route: RouteLocationNormalized) => !!(route.meta.pageTransition ?? defaultPageTransition)
    const hookToWait = hasTransition(from) && hasTransition(to) ? 'page:transition:finish' : 'page:loading:end'
    return new Promise((resolve) => {
      nuxtApp.hooks.hookOnce(hookToWait, () => {
        requestAnimationFrame(() => resolve(_calculatePosition(to, 'instant', position)))
      })
    })
  },
  routes: (_routes) => {
    const {
      public: { languageId },
    } = useRuntimeConfig()

    const ee: RouteRecordRaw[] = [
      {
        path: '/:name(beauty|ilukaubad|children|lastemaailm|interior|sisustus)',
        redirect: '/',
      },
      {
        name: 'hall-group',
        path: '/:name(ruumid|outlet)',
        component: () => import('~/components/hall-group/HallGroup.vue'),
      },
      {
        name: 'order-afterpayment',
        path: '/order/afterpayment',
        meta: {
          layout: 'checkout',
        },
        component: () => import('~/components/order/AfterPayment.vue'),
      },
    ]

    const fi: RouteRecordRaw[] = [
      {
        path: '/:name(beauty|kauneus_ja_kosmetiikka|children|lasten_maailma|interior)',
        redirect: '/',
      },
      {
        name: 'hall-group',
        path: '/:name(huoneet|outlet)',
        component: () => import('~/components/hall-group/HallGroup.vue'),
      },
      {
        name: 'order-avarda',
        path: '/order/avarda',
        meta: {
          layout: 'checkout',
        },
        component: () => import('~/components/order/Avarda.vue'),
      },
    ]

    const ru: RouteRecordRaw[] = [
      {
        path: '/:name(beauty|children|interior)',
        redirect: '/',
      },
      {
        name: 'hall-group',
        path: '/:name(комнаты|aутлет)',
        component: () => import('~/components/hall-group/HallGroup.vue'),
      },
      {
        name: 'order-afterpayment',
        path: '/order/afterpayment',
        meta: {
          layout: 'checkout',
        },
        component: () => import('~/components/order/AfterPayment.vue'),
      },
    ]

    return [
      _routes,
      {
        name: 'catchall',
        path: '/:catchall(.*)*',
        component: () => import('~/components/hall/Hall.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        name: 'hall',
        path: '/c/:id()/:slug()',
        component: () => import('~/components/hall/Hall.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        path: '/account',
        meta: { middleware: ['auth'] },
        component: () => import('~/components/account/Account.vue'),
        children: [
          {
            name: 'account',
            path: '',
            redirect: '/account/main',
          },
          {
            name: 'account-account-balance',
            path: 'account-balance',
            component: () => import('~/components/account/AccountBalance.vue'),
          },
          {
            name: 'account-main',
            path: 'main',
            component: () => import('~/components/account/AccountDetails.vue'),
          },
          {
            name: 'account-order-history',
            path: 'order-history',
            component: () => import('~/components/account/OrderHistory.vue'),
          },
          {
            name: 'account-wishlist',
            path: 'wishlist',
            component: () => import('~/components/account/Wishlist.vue'),
          },
        ],
      },
      {
        name: 'index',
        path: '/',
        component: () => import('~/components/index/Index.vue'),
      },
      {
        name: 'info',
        path: '/info/:slug()?',
        component: () => import('~/components/info/Info.vue'),
      },
      {
        name: 'login',
        path: '/login',
        component: () => import('~/components/login/LoginPage.vue'),
        meta: { middleware: ['unauthenticated'] },
      },
      {
        name: 'order-basket',
        path: '/order/basket',
        component: () => import('~/components/order/Basket.vue'),
      },
      {
        name: 'order-check',
        path: '/order/check',
        component: () => import('~/components/order/Check.vue'),
      },
      {
        name: 'order-payment',
        path: '/order/payment',
        meta: {
          layout: 'checkout',
        },
        component: () => import('~/components/order/Payment.vue'),
      },
      {
        name: 'order-transport',
        path: '/order/transport',
        meta: {
          layout: 'checkout',
        },
        component: () => import('~/components/order/Transport.vue'),
      },
      {
        name: 'product',
        path: '/p/:id([1-9][0-9]*)/:slug()?',
        component: () => import('~/components/product/Product.vue'),
      },
      {
        name: 'brand-hall',
        path: '/prods/:cat(interior|beauty|children)/brand/:slug()',
        component: () => import('~/components/hall/Hall.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        name: 'brand-hall-legacy',
        path: '/prods/brand/:slug()',
        component: () => import('~/components/hall/Hall.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        name: 'brand-list',
        path: '/prods/:category(interior|beauty|children)/brand',
        component: () => import('~/components/brand/BrandList.vue'),
      },
      {
        name: 'brand-list-legacy',
        path: '/prods/brand',
        component: () => import('~/components/brand/BrandList.vue'),
      },
      {
        name: 'prods-last',
        path: '/prods/last',
        component: () => import('~/components/hall/LastViewedProductsHall.vue'),
      },
      {
        name: 'register',
        path: '/register',
        component: () => import('~/components/register/Register.vue'),
        meta: { middleware: ['unauthenticated'] },
      },
      {
        name: 'search',
        path: '/search',
        component: () => import('~/components/search/Search.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        name: 'special-slug',
        path: '/special/:slug()',
        component: () => import('~/components/hall/SpecialHall.vue'),
        meta: { middleware: ['hall-sort-parameter'], hallSortOrder },
      },
      {
        name: 'link-wishlist',
        path: '/wishlist/:id()',
        component: () => import('~/components/link-wishlist/LinkWishlist.vue'),
      },
      {
        name: 'raffle',
        path: '/raffle',
        component: () => import('~/components/raffle/Raffle.vue'),
        meta: { middleware: ['auth'] },
      },
      ...(languageId === 1 ? ee : []),
      ...(languageId === 2 ? fi : []),
      ...(languageId === 3 ? ru : []),
    ]
  },
}
